import {ISideMenuService} from './side-menu.service.interface';
import {ServiceBase} from '../ServiceBase';
import {IServiceFactoryExtended} from "../service-factory-extended.interface";
import { action, observable, makeObservable } from "mobx";
import {IOverlayConsumer} from "../overlay/overlay-consumer.interface";
import {SideMenuItemConfigReader} from "./menu-items/side-menu-item-config-reader";
import {MenuItemKey} from "../configurations/config-data-sections/generated-config-const";
import {IMenuItemRenderer} from "../menu-items-common/renderer/menu-item.renderer.interface";
import {GenericSideMenuItemRenderer} from "./menu-items/generic-side-menu-item.renderer";

import {
  loadCustomMenuItemsRenderers
} from "../menu-items-common/renderer/dynamic-custom-menu-item-renderers-loader";
import {GenericCrossBrandAboveSearchItemRenderer} from './menu-items/generic-cross-brand-above-search-item.renderer';
import {IMenuItemActionFactoryService} from "../menu-item-action-factory/menu-item-action-factory.service.interface";

export class SideMenuService extends ServiceBase implements ISideMenuService, IOverlayConsumer {

  constructor(services: IServiceFactoryExtended,
              private readonly menuItemActionFactory: IMenuItemActionFactoryService) {
    super(services);

    makeObservable(this);
    this.services.overlay.registerConsumer(this);
    this._menuItemConfigReader = new SideMenuItemConfigReader(services);

    let rendererModules = require.context('./menu-items/custom-renderers', true, /.+\.renderer\.(ts|tsx)$/);
    this._customMenuItems = loadCustomMenuItemsRenderers(rendererModules.keys().map(rendererModules), this._menuItemConfigReader, this.services, this.menuItemActionFactory);


  }

  private readonly _menuItemConfigReader: SideMenuItemConfigReader;
  private readonly _customMenuItems: Record<MenuItemKey, IMenuItemRenderer>;


  getMenuItem(key: MenuItemKey): IMenuItemRenderer {
    let menuItemRenderer = this.services.regulation.current.menuItems.getSideMenuItem(key, this._menuItemConfigReader, this.menuItemActionFactory);
    if(menuItemRenderer) {
      return menuItemRenderer;
    }

    menuItemRenderer = this._customMenuItems[key];
    if(menuItemRenderer) {
      return menuItemRenderer;
    }

    return new GenericSideMenuItemRenderer(key, this._menuItemConfigReader, this.services, this.menuItemActionFactory);
  }

  getCrossBrandAboveSearchItemRenderer(crossBrandKey: MenuItemKey) {
      let menuItemRenderer = this.services.regulation.current.menuItems.getCrossBrandsAboveSearchItem(crossBrandKey, this._menuItemConfigReader, this.menuItemActionFactory );
      if(menuItemRenderer) {
          return menuItemRenderer;
      }
      return new GenericCrossBrandAboveSearchItemRenderer(crossBrandKey, this._menuItemConfigReader, this.services, this.menuItemActionFactory);
  }

  @observable.ref
  isMobileMenuOpen: boolean = false;

  @action.bound
  private _setIsMobileMenuOpen(shouldOpen: boolean) {
    this.isMobileMenuOpen = shouldOpen;
  }

  get needsOverlay(): boolean {
    return this.isMobileMenuOpen;
  }

  onOverlayClicked() {
    this.closeMobileMenu();
  }

  getOverlayZIndex(): number | undefined {
    return this.zIndex - 1;
  }

  get zIndex(): number {
    return 120;
  }

  async openMobileMenu(): Promise<void> {
    if (!this.isMobileMenuOpen) {
      (await this.services.userMenu).closeMenu();
      await (await this.services.gameRunner).currentRunningGame?.closeGame({
        shouldSuggestNextGame: false
      })

      this._setIsMobileMenuOpen(true);
    }
  }

  async closeMobileMenu(): Promise<void> {
    if (this.isMobileMenuOpen) {
      await (await this.services.gameSearch).closeSearchResult();
      this.closeMobileLanguageSwitcher();
      this._setIsMobileMenuOpen(false);
    }
  }



  @observable.ref
  isMobileLanguageSwitcher: boolean = false;

  @action.bound
  openMobileLanguageSwitcher(): void {
    this.isMobileLanguageSwitcher = true;
  }

  @action.bound
  closeMobileLanguageSwitcher(): void {
    this.isMobileLanguageSwitcher = false;
  }
}
